import { Rank, Suit, type HkCard } from "../gen/bazivan/v1/table_pb";
import { fa, isRed, suitSymbol } from "../game/cards";
import { lerp, outBack, outCubic } from "../scene/ease";
import { flyCard, kill, tween, tweenRaw, waitRaw } from "../scene/fly";
import {
  CARD_ASPECT, SEAT_RING, cardW, handCardTarget, pileCardScale, place, playedCardScale, playerCardW, seatPos, type Vec,
} from "../scene/geometry";
import { BRASS, BRASS_SOFT, CRIMSON, GLOW, IVORY, TableView } from "../scene/table";
import { nextOf, type HaftLocal } from "./state";

/**
 * صحنه‌ی هفت‌خبیث — پورتِ GameView.cs + GameView.Impl.cs روی TableView.
 *
 * فقط آن‌چه میزِ **آنلاین** صدا می‌زند (HaftOnlineController): چیدمان، رندرِ
 * دست‌ها و زمین، شمارنده‌ها، حلقه‌ی نوبت، پیکرِ خال/هدف/تعارف، دکمه‌های
 * تک‌کارت و مچ‌گیری، و انیمیشن‌ها: پروازِ کارت به زمین (با جلوه‌ی کارت‌های ویژه
 * سرِ فرود)، کشیدن، حکمِ خال، بمب، چکش، «تک‌کارت!». قدرتِ قهرمان، کیف و
 * جعبه‌ابزار عمداً نیستند — روی سرور هم نیستند.
 */
export type HaftCallbacks = {
  onCard(i: number): void;
  onDraw(): void;
  onPickSuit(s: Suit): void;
  onSeat(l: number): void;
  onPickOffered(i: number): void;
  onCallUno(): void;
  onCatch(): void;
  seatName(l: number): string;
};

const SP = { mirror: 1, bomb: 2, shield: 3, cloak: 4 } as const;

export class HaftScene extends TableView {
  private cb: HaftCallbacks;
  private lastSt: HaftLocal | null = null;
  private inputLocked = true;

  private deckStack!: HTMLElement; private deckGlow!: HTMLElement; private deckHintTxt!: HTMLElement; private deckCount!: HTMLElement;
  private deckHint = false;
  private discard!: HTMLElement;
  private suitGo!: HTMLElement; private suitTxt!: HTMLElement;
  private lastSuit: Suit | null = null; private lastTopKey: string | null = null;

  private suitPicker: HTMLElement | null = null;
  private unoBtn!: HTMLElement; private unoRing!: HTMLElement; private unoTimer: number | null = null;
  private catchBtn!: HTMLElement; private catchRing!: HTMLElement; private catchTimer: number | null = null;

  // «تعارفِ ۲»
  private offerRoot: HTMLElement | null = null;
  private offerCards: (HTMLElement | null)[] = [];
  private offerFrom = -1;
  private offerSway = false;
  private offerHiddenSeat = -1;

  constructor(host: HTMLElement, count: number, cb: HaftCallbacks) {
    super(host, count, "hft");
    this.cb = cb;
    this.build();
  }

  protected seatDFactor(): number { return 0.32; }
  protected plateH(): number { return 76; }

  setInputLocked(v: boolean): void { this.inputLocked = v; }

  private get pcardW(): number { return playerCardW(this.sc); }
  private get playedW(): number { return cardW(this.sc) * playedCardScale(this.sc); }

  // ---------------------------------------------------------------- ساخت

  protected buildCenter(): void {
    const R = this.sc.tableR;
    const deck = this.deck();
    const pileW = cardW(this.sc) * pileCardScale(this.sc), pileH = pileW * CARD_ASPECT;

    // دسته: سه پشتِ کارت + هاله‌ی «کارت بردار!» + شمارنده + دکمه‌ی کشیدن
    const anchor = document.createElement("div");
    anchor.className = "hft-deck";
    anchor.style.transform = place(deck);
    this.piles.appendChild(anchor);

    this.deckStack = document.createElement("div"); this.deckStack.className = "pile3";
    this.deckGlow = document.createElement("i"); this.deckGlow.className = "glow";
    this.deckGlow.style.cssText = `width:${pileW + 18}px;height:${pileH + 18}px;transform:translate(calc(-50% - 2px),calc(-50% - 2px))`;
    this.deckGlow.hidden = true;
    this.deckStack.appendChild(this.deckGlow);
    for (let i = 0; i < 3; i++) {
      const c = this.cardEl(null, true, pileW);
      c.style.transform = place({ x: -i * 2, y: i * 2 }, i * 2 - 2);
      this.deckStack.appendChild(c);
    }
    anchor.appendChild(this.deckStack);

    this.deckHintTxt = document.createElement("div"); this.deckHintTxt.className = "hint";
    this.deckHintTxt.textContent = "کارت بردار!";
    this.deckHintTxt.style.transform = `translate(-50%,-50%) translateY(${-pileH * 0.74}px)`;
    this.deckHintTxt.hidden = true;
    anchor.appendChild(this.deckHintTxt);

    this.deckCount = document.createElement("div"); this.deckCount.className = "cnt";
    this.deckCount.style.transform = `translate(-50%,-50%) translateY(${pileH * 0.72}px)`;
    anchor.appendChild(this.deckCount);

    const btn = document.createElement("button"); btn.className = "hit";
    btn.style.width = `${pileW * 1.25}px`; btn.style.height = `${pileH * 1.2}px`;
    btn.setAttribute("aria-label", "کارت بکش");
    btn.addEventListener("click", () => this.cb.onDraw());
    anchor.appendChild(btn);

    // زمین: کارتِ رو وسطِ رومیزی
    this.discard = document.createElement("div");
    this.discard.className = "hft-discard";
    this.discard.style.transform = place({ x: 0, y: 0 });
    this.piles.appendChild(this.discard);

    // نشانگرِ خال: دایره‌ی طلایی ۵۲ با خالِ سفید + برچسبِ «خالِ فعلی»
    const suitPos = { x: R * 0.24 * this.sc.aspect, y: R * 0.30 };
    this.suitGo = document.createElement("div"); this.suitGo.className = "hft-suit";
    this.suitGo.style.transform = place(suitPos);
    this.suitTxt = document.createElement("span");
    this.suitGo.appendChild(this.suitTxt);
    this.piles.appendChild(this.suitGo);
    const lbl = document.createElement("div"); lbl.className = "hft-suitlbl"; lbl.textContent = "خال فعلی";
    lbl.style.transform = place({ x: suitPos.x, y: suitPos.y - 37 });
    this.piles.appendChild(lbl);

    this.buildActionButtons();
    this.lastTopKey = null;
    if (this.lastSt) this.renderPiles(this.lastSt);
  }

  protected redraw(): void {
    const st = this.lastSt; if (!st) return;
    this.layoutSeats(st); this.render(st); this.renderPiles(st); this.updateCounts(st);
    if (st.awaitingSuit) this.openSuitPicker();
    if (st.awaitingTarget) this.openTargetPicker();
    if (st.awaitingOffer && st.offerFrom >= 0) this.openOffer(st, st.offerFrom);
  }

  /** تک‌کارت: مربعِ گوشه‌گردِ قرمز با لودرِ بوردری؛ مچ‌گیری: بزرگ، با چکش، حلقه و پالس. */
  private buildActionButtons(): void {
    const R = this.sc.tableR;
    const ud = Math.min(150, Math.max(104, R * 0.5));
    this.unoBtn = document.createElement("button");
    this.unoBtn.className = "hft-uno";
    this.unoBtn.style.width = this.unoBtn.style.height = `${ud}px`;
    this.unoBtn.innerHTML = `<i class="ring"></i><span class="body" style="font-size:${ud * 0.15}px">تک‌کارت!</span>`;
    this.unoBtn.hidden = true;
    this.unoBtn.addEventListener("click", () => this.cb.onCallUno());
    this.ui.appendChild(this.unoBtn);
    this.unoRing = this.unoBtn.querySelector(".ring")!;

    const cd = Math.min(176, Math.max(128, R * 0.62));
    this.catchBtn = document.createElement("button");
    this.catchBtn.className = "hft-catch";
    this.catchBtn.style.width = this.catchBtn.style.height = `${cd}px`;
    this.catchBtn.style.transform = place({ x: this.sc.halfW - cd * 0.62 - 14, y: R * 0.18 });
    this.catchBtn.innerHTML = `<i class="ring"></i><span class="body">${hammerHtml(cd * 0.4, -38)}<span class="t" style="font-size:${cd * 0.135}px">مچش بگیر!</span></span>`;
    this.catchBtn.hidden = true;
    this.catchBtn.addEventListener("click", () => this.cb.onCatch());
    this.ui.appendChild(this.catchBtn);
    this.catchRing = this.catchBtn.querySelector(".ring")!;
  }

  // ---------------------------------------------------------------- نشست‌ها

  layoutSeats(st: HaftLocal): void {
    this.lastSt = st;
    this.layoutSeatsFrom({ names: st.names, bots: st.bots, avatars: st.avatars, subscribers: st.subscribers, places: st.places, countries: st.countries, connected: st.connected, watching: st.watching, playerIds: st.playerIds });
    this.onSeatClick((l) => this.cb.onSeat(l));
  }

  updateCounts(st: HaftLocal): void {
    for (let l = st.watching ? 0 : 1; l < this.count; l++) {
      const s = this.seatEls[l]; if (!s) continue;
      if (s.count) s.count.textContent = `${fa(st.counts[l] ?? 0)} کارت`;
      if (s.score) s.score.textContent = `${fa(st.scores[l] ?? 0)} امتیاز`;
      this.setConnected(l, !!st.connected[l]);
    }
  }

  private updateTurnRings(st: HaftLocal): void {
    if (st.awaitingTarget) return;   // هایلایتِ انتخابِ هدف را خراب نکن
    for (let l = 0; l < this.count; l++) this.setRing(l, !st.roundOver && st.turn === l);
  }

  // ---------------------------------------------------------------- رندر

  render(st: HaftLocal): void {
    this.lastSt = st;
    this.renderPlayerHand(st);
    this.renderOpponentHands(st);
    this.updateCounts(st);
    this.updateTurnRings(st);
    this.suitTxt.textContent = suitSymbol[st.topSuit] ?? "";
  }

  private setDeckHint(on: boolean): void {
    if (this.deckHint === on) return;
    this.deckHint = on;
    this.deckGlow.hidden = !on;
    this.deckHintTxt.hidden = !on;
    this.deckStack.classList.toggle("hint", on);
  }

  /** RenderPlayerHand: بادبزنِ نرمال‌شده با سقفِ زاویه و قوس؛ کارتِ بازی‌شدنی بالا و با هاله. */
  private renderPlayerHand(st: HaftLocal): void {
    this.hand.innerHTML = "";
    this.setDeckHint(false);
    if (this.handHidden || this.offerHiddenSeat === 0) return;
    const hand = st.myHand, n = hand.length;
    if (n === 0) return;

    const w = this.pcardW, cardH = w * CARD_ASPECT;
    const maxSpan = this.sc.halfW * 2 * 0.8 - w;
    const step = n > 1 ? Math.min(w * 0.4, maxSpan / (n - 1)) : 0;
    const maxM = (n - 1) / 2;
    const edgeRot = Math.min(24, maxM * 8);
    const arcDepth = w * Math.min(0.63, 0.07 * maxM * maxM);
    const baseY = Math.max(cardH * 0.85, arcDepth + cardH * 0.58);
    const myTurn = st.myTurn && !this.inputLocked;
    let anyPlayable = false;

    for (let i = 0; i < n; i++) {
      const c = hand[i]!;
      const m = i - (n - 1) / 2;
      const playable = myTurn && !st.awaitingSuit && !st.awaitingTarget && !!st.playable[i];
      anyPlayable ||= playable;
      const lift = playable ? w * 0.16 : 0;
      const tN = maxM > 0 ? m / maxM : 0;
      const face = this.cardEl(c.card, false, w, c.special);
      face.classList.add("mine");
      if (playable) face.classList.add("playable");
      face.style.transform = place({ x: m * step, y: -this.sc.halfH + baseY + lift - tN * tN * arcDepth }, -tN * edgeRot);
      face.style.zIndex = String(i);
      if (myTurn) this.armCard(face, this.cardLabelFor(c.card, c.special), () => this.cb.onCard(i));
      this.hand.appendChild(face);
    }

    // هیچ کارتی بازی‌پذیر نیست و نوبتِ آزادِ توست → تنها حرکت برداشتن از دسته است
    this.setDeckHint(myTurn && !st.awaitingSuit && !st.awaitingTarget && !anyPlayable && st.deckLeft > 0);

    // نشانِ تعدادِ کارت کنارِ دست
    const span = step * (n - 1) + w;
    const badge = document.createElement("div"); badge.className = "hft-handn";
    badge.textContent = `${fa(n)} کارت`;
    badge.style.transform = place({ x: -(span / 2) - 52, y: -this.sc.halfH + 48 });
    this.hand.appendChild(badge);
    this.armHand();
  }

  /** RenderOpponentHands: بادبزنِ پشت‌ورو، حداکثر ۷ برگ. */
  private renderOpponentHands(st: HaftLocal): void {
    this.opp.innerHTML = "";
    if (this.handHidden) return;
    const cw = cardW(this.sc);
    // تماشاچی دستی ندارد، پس صندلیِ ۰ هم بادبزنِ پشت‌وروی خودش را می‌گیرد.
    for (let p = st.watching ? 0 : 1; p < this.count; p++) {
      if (p === this.offerHiddenSeat) continue;
      const shown = Math.min(st.counts[p] ?? 0, 7);
      for (let j = 0; j < shown; j++) {
        const { pos, rot } = handCardTarget(this.sc, p, this.count, shown, j);
        const c = this.cardEl(null, true, cw);
        c.style.transform = place(pos, rot);
        c.style.zIndex = String(j);
        this.opp.appendChild(c);
      }
    }
  }

  /** RenderPiles: سایه + قابِ برنجی + کارتِ رو (۳- درجه)، خال، شمارِ دسته، پانچِ فرود. */
  renderPiles(st: HaftLocal): void {
    this.lastSt = st;
    this.discard.innerHTML = "";
    this.deckCount.textContent = `${fa(st.deckLeft)} کارت`;
    if (!st.top) { this.lastTopKey = null; return; }
    const pw = this.playedW, ph = pw * CARD_ASPECT;
    const sh = document.createElement("i"); sh.className = "shadow";
    sh.style.cssText = `width:${pw * 1.2}px;height:${pw * 0.42}px;transform:translate(-50%,-50%) translateY(${ph * 0.44}px)`;
    const fr = document.createElement("i"); fr.className = "frame";
    fr.style.cssText = `width:${pw + 7}px;height:${ph + 7}px;transform:translate(-50%,-50%) rotate(3deg)`;
    const top = this.cardEl(st.top.card, false, pw, st.top.special);
    top.style.transform = place({ x: 0, y: 0 }, -3);
    this.discard.append(sh, fr, top);

    this.suitTxt.textContent = suitSymbol[st.topSuit] ?? "";
    if (this.lastSuit === null || this.lastSuit !== st.topSuit) {
      if (this.lastSuit !== null) void this.suitPop();
      this.lastSuit = st.topSuit;
    }
    const key = `${st.top.card?.suit}-${st.top.card?.rank}-${st.top.special}`;
    if (this.lastTopKey !== key) { this.lastTopKey = key; void this.playedPunch(); }
  }

  private suitPop(): Promise<void> {
    return tweenRaw(300, (t) => { const k = 1 - outCubic(t); this.suitGo.style.scale = String(1 + 0.55 * k); })
      .then(() => { this.suitGo.style.scale = ""; });
  }

  private playedPunch(): Promise<void> {
    return tweenRaw(180, (t) => { this.discard.style.scale = String(1 + 0.13 * (1 - outCubic(t))); })
      .then(() => { this.discard.style.scale = ""; });
  }

  // ---------------------------------------------------------------- دکمه‌ها

  /** ShowUnoButton: جای رندومِ بالای دست، حلقه‌ی شمارشِ معکوس که به پایان قرمز می‌شود. */
  showUnoButton(show: boolean, seconds: number): void {
    if (this.unoTimer) { cancelAnimationFrame(this.unoTimer); this.unoTimer = null; }
    this.unoBtn.hidden = !show;
    if (!show) return;
    const d = parseFloat(this.unoBtn.style.width);
    const rx = Math.max(20, this.sc.halfW - d * 0.6 - 24);
    const yLo = -this.sc.halfH * 0.05, yHi = this.sc.halfH * 0.5;
    this.unoBtn.style.transform = place({ x: lerp(-rx, rx, Math.random()), y: lerp(yLo, yHi, Math.random()) });
    this.unoBtn.classList.remove("in"); void this.unoBtn.offsetWidth; this.unoBtn.classList.add("in");
    const total = Math.max(0.5, seconds) * 1000, t0 = performance.now();
    const step = () => {
      const k = Math.max(0, 1 - (performance.now() - t0) / total);
      this.unoRing.style.setProperty("--f", String(k));
      this.unoRing.style.setProperty("--rc", k > 0.5 ? GLOW : k > 0.2 ? BRASS_SOFT : CRIMSON);
      if (k > 0 && !this.unoBtn.hidden) this.unoTimer = requestAnimationFrame(step);
    };
    step();
  }

  showCatchButton(show: boolean, seconds: number): void {
    if (this.catchTimer) { cancelAnimationFrame(this.catchTimer); this.catchTimer = null; }
    this.catchBtn.hidden = !show;
    if (!show) return;
    this.catchBtn.classList.remove("in"); void this.catchBtn.offsetWidth; this.catchBtn.classList.add("in");
    if (seconds > 0) {
      const total = seconds * 1000, t0 = performance.now();
      const step = () => {
        const k = Math.max(0, 1 - (performance.now() - t0) / total);
        this.catchRing.style.setProperty("--f", String(k));
        if (k > 0 && !this.catchBtn.hidden) this.catchTimer = requestAnimationFrame(step);
      };
      step();
    } else this.catchRing.style.setProperty("--f", "1");
  }

  // ---------------------------------------------------------------- پیکرها

  /** OpenSuitPicker: پنلِ تیره زیرِ مرکز با چهار دکمه‌ی خال. */
  openSuitPicker(): void {
    this.closeSuitPicker();
    const R = this.sc.tableR;
    const p = document.createElement("div");
    p.className = "hft-suitpicker in";
    p.style.width = `${R * 1.5}px`; p.style.height = `${R * 0.66}px`;
    p.style.transform = place({ x: 0, y: -R * 0.1 });
    p.innerHTML = `<div class="t">حکم را انتخاب کن</div>`;
    const order: Suit[] = [Suit.HEARTS, Suit.DIAMONDS, Suit.SPADES, Suit.CLUBS];
    order.forEach((s, i) => {
      const b = document.createElement("button");
      b.className = `s ${isRed(s) ? "red" : ""}`;
      b.textContent = suitSymbol[s] ?? "";
      b.style.width = `${R * 0.28}px`; b.style.height = `${R * 0.32}px`;
      b.style.transform = `translate(-50%,-50%) translate(${(i - 1.5) * R * 0.34}px,${R * 0.06}px)`;
      b.addEventListener("click", () => this.cb.onPickSuit(s));
      p.appendChild(b);
    });
    this.ov.appendChild(p);
    this.suitPicker = p;
  }

  closeSuitPicker(): void { kill(this.suitPicker); this.suitPicker = null; }

  /** OpenTargetPicker: حریف‌ها با حلقه‌ی سرخ مشخص می‌شوند؛ لمسِ صندلی = انتخاب. */
  openTargetPicker(): void { for (let p = 1; p < this.count; p++) this.setRing(p, true, true); }

  /** هدفِ «۲» انتخاب شد: حلقه‌ی سرخِ همان صندلی یک لحظه روشن می‌شود. */
  flashTarget(l: number): void {
    if (l < 0 || l >= this.count) return;
    this.setRing(l, true, true);
    setTimeout(() => this.setRing(l, false, false), 900);
  }
  closeTargetPicker(): void { for (let p = 1; p < this.count; p++) this.setRing(p, false, false); }

  // ---- «تعارفِ ۲»: دستِ رو‌بسته‌ی بازی‌کننده‌ی ۲ جلوی قربانی ----

  private offerOrigin(p: number): Vec {
    return p === 0 ? { x: 0, y: -this.sc.halfH + this.pcardW * CARD_ASPECT * 0.6 }
                   : seatPos(this.sc, p, this.count, this.sc.tableR * SEAT_RING);
  }
  private offerTargetPos(to: number): Vec {
    return to === 0 ? { x: 0, y: -this.sc.halfH * 0.36 } : seatPos(this.sc, to, this.count, this.sc.tableR * 0.6);
  }

  private buildOfferFan(from: number, count: number, origin: Vec): void {
    this.closeOffer();
    this.offerFrom = from;
    const root = document.createElement("div");
    root.className = "hft-offer";
    root.style.transform = place(origin);
    this.ov.appendChild(root);
    this.offerRoot = root;
    const w = cardW(this.sc) * pileCardScale(this.sc) * 0.95;
    const step = Math.min(w * 0.55, (this.sc.halfW * 1.5 - w) / Math.max(1, count - 1));
    for (let i = 0; i < count; i++) {
      const m = i - (count - 1) / 2;
      const c = this.cardEl(null, true, w);
      c.style.transform = place({ x: m * step, y: -(m * m) * w * 0.03 }, -m * 4);
      root.appendChild(c);
      this.offerCards.push(c);
    }
  }

  private slideOffer(to: Vec, durMs: number): Promise<void> {
    const root = this.offerRoot; if (!root) return Promise.resolve();
    const from = this.currentPos(root);
    return tween(durMs, (k) => {
      const p = outCubic(k);
      root.style.transform = place({ x: lerp(from.x, to.x, p), y: lerp(from.y, to.y, p) });
    });
  }

  /** OpenOffer: بادبزن از دستِ صاحبش جلوی من می‌آید، لمس‌پذیر می‌شود و تکان می‌خورد. */
  openOffer(st: HaftLocal, from: number): void {
    this.buildOfferFan(from, st.counts[from] ?? st.offerCount, this.offerOrigin(from));
    this.offerHiddenSeat = from;
    this.renderPlayerHand(st); this.renderOpponentHands(st);
    const lbl = document.createElement("div"); lbl.className = "lbl"; lbl.textContent = "یکی بردار…";
    lbl.style.transform = `translate(-50%,-50%) translateY(${-cardW(this.sc) * pileCardScale(this.sc) * CARD_ASPECT * 0.85}px)`;
    this.offerRoot!.appendChild(lbl);
    void this.openOfferCo();
  }

  private async openOfferCo(): Promise<void> {
    await this.slideOffer(this.offerTargetPos(0), 450);
    this.offerCards.forEach((c, i) => {
      if (!c) return;
      c.style.pointerEvents = "auto"; c.style.cursor = "pointer";
      c.addEventListener("click", () => this.cb.onPickOffered(i));
    });
    this.offerSway = true;
    const base = this.offerTargetPos(0), t0 = performance.now();
    const sway = () => {
      if (!this.offerRoot || !this.offerSway) return;
      const k = Math.sin((performance.now() - t0) / 1000 * 2.2) * 4;
      this.offerRoot.style.transform = place({ x: base.x, y: base.y + k });
      requestAnimationFrame(sway);
    };
    sway();
  }

  /** AnimOfferTake: کارتِ برداشته به دستِ من می‌پرد، بقیه پیشِ صاحبش برمی‌گردند. */
  async animOfferTake(pick: number): Promise<void> {
    const root = this.offerRoot; if (!root) return;
    this.offerSway = false;
    const rootPos = this.currentPos(root);
    const picked = this.offerCards[pick];
    if (picked) {
      const abs = { x: rootPos.x + this.currentPos(picked).x, y: rootPos.y + this.currentPos(picked).y };
      this.fly.appendChild(picked);
      this.offerCards[pick] = null;
      void flyCard(picked, abs, this.myOrigin(), 0, 320).then(() => kill(picked));
    }
    const back = this.offerOrigin(this.offerFrom);
    await tween(400, (k) => {
      const p = outCubic(k);
      root.style.transform = place({ x: lerp(rootPos.x, back.x, p), y: lerp(rootPos.y, back.y, p) });
      root.style.opacity = String(1 - p * 0.9);
    });
    this.closeOffer();
  }

  closeOffer(): void {
    kill(this.offerRoot); this.offerRoot = null;
    this.offerCards = [];
    this.offerHiddenSeat = -1;
    this.offerSway = false;
  }

  // ---------------------------------------------------------------- انیمیشن‌ها

  /** AnimDeal: دست‌ها پنهان، پخشِ پشت‌ورو از دسته، بعد رندر. */
  async animDeal(st: HaftLocal, perPlayer: number): Promise<void> {
    this.handHidden = true; this.render(st);
    await this.dealBacks(0, perPlayer);
    this.handHidden = false; this.render(st);
  }

  /** AnimFlyToDiscard: از صندلی به زمین، ۳- درجه، ۲۸۰؛ سرِ فرود جلوه‌ی کارتِ ویژه. */
  async animFlyToDiscard(st: HaftLocal, from: number, card: HkCard): Promise<void> {
    const origin = from === 0 ? this.myOrigin() : seatPos(this.sc, from, this.count, this.sc.tableR * SEAT_RING);
    const c = this.flyEl(card.card, false, this.playedW, card.special);
    await flyCard(c, origin, { x: 0, y: 0 }, -3, 280);
    kill(c);
    const sp = card.special ?? 0;
    if (sp === SP.bomb) void this.bombFx();
    else if (sp === SP.mirror) void this.mirrorFx();
    else if (sp === SP.shield) void this.shieldFx(from);
    else if (sp === SP.cloak) void this.cloakFx(from);
    else if (card.card?.rank === Rank.SEVEN) void this.sevenFx(st, from);
  }

  /** SevenEffect: موجِ قرمز، نمادِ «جریمه»، از زنجیرِ ۲ لرزش، از ۳ پروازِ کارت‌های جریمه. */
  sevenEffect(chain: number, pending: number, victim: number): void {
    const pw = this.playedW, end = pw * (1.9 + 0.45 * chain);
    void this.pulse({ x: 0, y: 0 }, CRIMSON, pw * 1.05, end, 500 + 50 * chain, 0.5);
    if (chain >= 2) void this.pulse({ x: 0, y: 0 }, CRIMSON, pw * 1.3, end * 1.25, 700, 0.42);
    void this.centerGlyphBurst("جریمه", CRIMSON, null, 1.4 + 0.35 * (chain - 1));
    if (chain >= 2) void this.shake(220 + 80 * chain, 5 + 4 * chain);
    if (chain >= 3) void this.flyPenaltyCards(pending, victim);
  }

  private async flyPenaltyCards(count: number, victim: number): Promise<void> {
    if (victim < 0 || victim >= this.count) return;
    const to = this.seatFx(victim);
    for (let i = 0; i < Math.min(10, Math.max(1, count)); i++) {
      const c = this.flyEl(null, true, cardW(this.sc) * 0.9);
      void tweenRaw(420, (k) => {
        const p = outCubic(k);
        c.style.transform = place({ x: lerp(0, to.x, p), y: lerp(0, to.y, p) + Math.sin(p * Math.PI) * this.sc.tableR * 0.22 }, p * 200, lerp(1, 0.7, p));
      }).then(() => kill(c));
      await waitRaw(110);
    }
  }

  /** CenterGlyphBurst: نمادِ بزرگِ وسطِ میز — ظاهر، بزرگ، محو (۷۲۰ خام). */
  private centerGlyphBurst(glyph: string, col: string, sub: string | null, maxScale = 1.5): Promise<void> {
    const pw = this.playedW;
    const h = document.createElement("div"); h.className = "hft-glyph";
    h.style.color = col;
    const word = glyph.length > 1;
    h.innerHTML = `<b style="font-size:${word ? pw * 0.42 : pw * 0.82}px">${glyph}</b>${sub ? `<i style="font-size:${pw * 0.26}px;transform:translateY(${pw * 0.72}px)">${sub}</i>` : ""}`;
    this.fly.appendChild(h);
    return tweenRaw(720, (p) => {
      const grow = lerp(0.5, maxScale, outCubic(Math.min(1, p / 0.45)));
      h.style.transform = place({ x: 0, y: 0 }, 0, grow);
      h.style.opacity = String(p < 0.45 ? 1 : 1 - (p - 0.45) / 0.55);
    }).then(() => kill(h));
  }

  /** FlashRuling: پالسِ نشانگرِ خال + حکمِ بزرگِ خال وسطِ میز (سرباز). */
  flashRuling(suit: Suit): void {
    void tweenRaw(600, (t) => { this.suitGo.style.scale = String(1 + 0.35 * Math.sin(t * Math.PI)); }).then(() => { this.suitGo.style.scale = ""; });
    const col = isRed(suit) ? CRIMSON : IVORY;
    const pw = this.playedW;
    void this.pulse({ x: 0, y: 0 }, col, pw * 1.05, pw * 2.2, 600, 0.55);
    void this.centerGlyphBurst(suitSymbol[suit] ?? "", col, "حکم");
  }

  /** ShowUnoFx: «تک‌کارتِ فلانی!» بالای میز، پاپ و محو. */
  showUnoFx(who: string): void {
    const t = document.createElement("div"); t.className = "hft-unofx";
    t.textContent = who ? `تک‌کارت ${who}!` : "تک‌کارت!";
    t.style.transform = place({ x: 0, y: this.sc.tableR * 0.75 }, 0, 0);
    this.ov.appendChild(t);
    void this.popAndFade(t, { x: 0, y: this.sc.tableR * 0.75 }, 1400);
  }

  private async popAndFade(el: HTMLElement, at: Vec, lifeMs: number): Promise<void> {
    await tweenRaw(250, (k) => { el.style.transform = place(at, 0, outBack(k)); });
    el.style.transform = place(at);
    await waitRaw(lifeMs - 550);
    await tweenRaw(300, (k) => { el.style.opacity = String(1 - k); });
    kill(el);
  }

  /** HammerFx: چکش از بالا خیز می‌گیرد و می‌کوبد توی صورتِ صندلی — ستاره‌ی برخورد و لهیدگی. */
  async hammerFx(seat: number): Promise<void> {
    const s = this.seatEls[seat]; if (!s) return;
    const face = s.pos;
    const h = Math.min(150, Math.max(90, this.sc.tableR * 0.55));
    const hm = document.createElement("div"); hm.className = "hft-hammer";
    hm.style.width = hm.style.height = `${h}px`;
    hm.innerHTML = hammerHtml(h, 0);
    this.ov.appendChild(hm);
    const from = { x: face.x + h * 0.9, y: face.y + h * 1.05 };
    const hit = { x: face.x - h * 0.07, y: face.y - h * 0.28 };
    hm.style.transform = place(from, 55);
    await tweenRaw(100, (k) => { hm.style.transform = place(from, lerp(55, 80, k)); });
    await tweenRaw(110, (k) => { const p = k * k; hm.style.transform = place({ x: lerp(from.x, hit.x, p), y: lerp(from.y, hit.y, p) }, lerp(80, -18, p)); });
    hm.style.transform = place(hit, -18);
    this.impactBurst(face);
    void this.faceSquash(seat);
    await tweenRaw(220, (k) => { hm.style.transform = place(hit, lerp(-18, 26, outCubic(k))); hm.style.opacity = String(1 - k); });
    kill(hm);
  }

  private impactBurst(at: Vec): void {
    const root = document.createElement("div"); root.className = "hft-pow";
    root.style.transform = place(at);
    root.innerHTML = `<i class="halo"></i>` + Array.from({ length: 7 }, (_, i) => `<i class="ray" style="rotate:${-(i * (360 / 7) + 12)}deg"></i>`).join("");
    this.ov.appendChild(root);
    const R = Math.min(80, Math.max(46, this.sc.tableR * 0.3));
    const halo = root.querySelector<HTMLElement>(".halo")!, rays = root.querySelectorAll<HTMLElement>(".ray");
    void tweenRaw(340, (k) => {
      const p = outCubic(k);
      halo.style.width = halo.style.height = `${lerp(10, R * 1.7, p)}px`;
      rays.forEach((r) => { r.style.setProperty("--d", `${R * p}px`); });
      root.style.opacity = String((1 - p) * 0.9);
    }).then(() => kill(root));
  }

  private faceSquash(seat: number): Promise<void> {
    const s = this.seatEls[seat]; if (!s) return Promise.resolve();
    return tweenRaw(420, (p) => {
      const spring = Math.sin(p * Math.PI * 3) * (1 - p);
      const jx = (Math.random() * 2 - 1) * (1 - p) * 5, jy = (Math.random() * 2 - 1) * (1 - p) * 5;
      s.root.style.transform = `${place({ x: s.pos.x + jx, y: s.pos.y + jy })} scale(${1 + spring * 0.22}, ${1 - spring * 0.3})`;
    }).then(() => { s.root.style.transform = place(s.pos); });
  }

  // ---- جلوه‌ی کارت‌های ویژه، سرِ فرود ----

  private async bombFx(): Promise<void> {
    void this.shake(750, 18);
    const flash = document.createElement("div"); flash.className = "hft-flash";
    this.ov.appendChild(flash);
    const cracks = this.buildCracks({ x: 0, y: 0 });
    await tweenRaw(90, (k) => { cracks.style.transform = place({ x: 0, y: 0 }, 0, lerp(0.4, 1, k)); });
    await tweenRaw(280, (k) => { flash.style.opacity = String(1 - k); });
    kill(flash);
    await waitRaw(900);
    await tweenRaw(600, (k) => { cracks.style.opacity = String(1 - k); });
    kill(cracks);
  }

  /** ترکِ شیشه‌ی رویه‌ای: خط‌های شکسته‌ی شعاعی با شاخه — هر بمب الگوی تازه. */
  private buildCracks(center: Vec): HTMLElement {
    const root = document.createElement("div"); root.className = "hft-cracks";
    root.style.transform = place(center);
    const R = this.sc.tableR;
    const rnd = (a: number, b: number) => a + Math.random() * (b - a);
    const line = (pos: Vec, angle: number, len: number, w: number, alpha: number) => {
      const mk = (cls: string, dx: number, dy: number, ww: number, al: number) => {
        const l = document.createElement("i"); l.className = cls;
        l.style.cssText = `width:${len}px;height:${ww}px;opacity:${al};transform:translate(${pos.x + dx}px,${-(pos.y + dy)}px) rotate(${-angle}deg)`;
        root.appendChild(l);
      };
      mk("cs", 1.5, -1.5, w + 2, alpha * 0.35);
      mk("c", 0, 0, w, alpha);
    };
    for (let i = 0; i < 9; i++) {
      let angle = i * 40 + rnd(-16, 16), pos = { x: 0, y: 0 }, w = 4.5;
      const segs = 4 + Math.floor(Math.random() * 3);
      for (let s = 0; s < segs; s++) {
        const len = R * rnd(0.1, 0.22) * (1 - s * 0.1);
        line(pos, angle, len, w, 0.9 - s * 0.1);
        if (s >= 1 && Math.random() < 0.45) {
          let bA = angle + (Math.random() < 0.5 ? 1 : -1) * rnd(35, 60), bp = pos, bw = w * 0.6;
          for (let b = 0; b < 2; b++) {
            const bl = len * rnd(0.35, 0.6);
            line(bp, bA, bl, bw, 0.55);
            bp = { x: bp.x + Math.cos(bA * Math.PI / 180) * bl, y: bp.y + Math.sin(bA * Math.PI / 180) * bl };
            bA += rnd(-24, 24); bw *= 0.7;
          }
        }
        pos = { x: pos.x + Math.cos(angle * Math.PI / 180) * len, y: pos.y + Math.sin(angle * Math.PI / 180) * len };
        angle += rnd(-26, 26); w *= 0.82;
      }
    }
    const hub = document.createElement("i"); hub.className = "hub"; root.appendChild(hub);
    this.ov.appendChild(root);
    return root;
  }

  private async mirrorFx(): Promise<void> {
    void this.pulse({ x: 0, y: 0 }, "#ccebff", this.sc.tableR * 0.25, this.sc.tableR * 1.7, 550, 0.35);
    const glint = document.createElement("i"); glint.className = "hft-glint";
    glint.style.width = `${this.sc.halfW * 0.25}px`; glint.style.height = `${this.sc.halfH * 2.6}px`;
    this.ov.appendChild(glint);
    const ghost = this.cardEl(null, true, cardW(this.sc) * 1.15);
    this.ov.appendChild(ghost);
    await tweenRaw(500, (p) => {
      glint.style.transform = place({ x: lerp(-this.sc.halfW * 1.4, this.sc.halfW * 1.4, p), y: 0 }, -18);
      ghost.style.transform = `${place({ x: p * this.sc.tableR * 0.45, y: 0 })} scaleX(${Math.cos(p * Math.PI)})`;
      ghost.style.opacity = String(0.7 * (1 - p));
    });
    kill(glint); kill(ghost);
  }

  private async shieldFx(p: number): Promise<void> {
    const pos = this.seatFx(p);
    void this.pulse(pos, BRASS, this.sc.tableR * 0.3, this.sc.tableR, 500, 0.4);
    const d = this.sc.tableR * 0.72;
    const ring = document.createElement("i"); ring.className = "hft-shield";
    ring.style.transform = place(pos);
    this.ov.appendChild(ring);
    await tweenRaw(250, (k) => { const s = d * outBack(k); ring.style.width = ring.style.height = `${s}px`; });
    await waitRaw(550);
    await tweenRaw(300, (k) => { ring.style.opacity = String(1 - k); });
    kill(ring);
  }

  private async cloakFx(p: number): Promise<void> {
    const pos = this.seatFx(p), R = this.sc.tableR;
    const puffs: { el: HTMLElement; start: Vec }[] = [];
    for (let i = 0; i < 7; i++) {
      const a = i * (360 / 7) * Math.PI / 180;
      const start = { x: pos.x + Math.cos(a) * R * 0.55, y: pos.y + Math.sin(a) * R * 0.55 };
      const el = document.createElement("i"); el.className = "hft-puff";
      el.style.width = el.style.height = `${R * 0.3}px`;
      el.style.transform = place(start);
      this.ov.appendChild(el);
      puffs.push({ el, start });
    }
    await tweenRaw(550, (t) => {
      const k = outCubic(t);
      for (const { el, start } of puffs) {
        el.style.transform = place({ x: lerp(start.x, pos.x, k), y: lerp(start.y, pos.y, k) });
        const s = R * 0.3 * (1 - 0.55 * k);
        el.style.width = el.style.height = `${s}px`;
        el.style.opacity = String(0.55 * (1 - k * 0.75));
      }
    });
    puffs.forEach((p2) => kill(p2.el));
  }

  /** SevenFx: موجِ قرمز + برچسبِ جمعِ جریمه که در قوس به‌سمتِ قربانیِ بعدی می‌رود. */
  private async sevenFx(st: HaftLocal, from: number): Promise<void> {
    void this.pulse({ x: 0, y: 0 }, CRIMSON, this.sc.tableR * 0.2, this.sc.tableR * 1.2, 450, 0.5);
    const to = this.seatFx(nextOf(st, from));
    // این افکت پیش از نشستنِ وضعیت اجرا می‌شود، پس ۲ِ همین ۷ هنوز روی pendingDraw نیست
    const total = st.pendingDraw + 2;
    const tag = document.createElement("div"); tag.className = "hft-plus";
    tag.textContent = `+${fa(total)}`;
    this.ov.appendChild(tag);
    await tweenRaw(500, (t) => {
      const p = outCubic(t);
      tag.style.transform = place({ x: lerp(0, to.x, p), y: lerp(0, to.y, p) + Math.sin(p * Math.PI) * this.sc.tableR * 0.22 });
      tag.style.opacity = String(p < 0.75 ? 1 : 1 - (p - 0.75) / 0.25);
    });
    kill(tag);
  }
}

/** چکشِ رسم‌شده با شِیپ‌ها: دسته‌ی چوبی + سرِ فلزی با برق. */
function hammerHtml(h: number, tiltDeg: number): string {
  return `<span class="hammer" style="width:${h}px;height:${h}px;rotate:${-tiltDeg}deg">` +
    `<i class="handle" style="width:${h * 0.16}px;height:${h * 0.84}px;transform:translate(-50%,-50%) translateY(${h * 0.14}px)"></i>` +
    `<i class="head" style="width:${h * 0.74}px;height:${h * 0.34}px;transform:translate(-50%,-50%) translateY(${-h * 0.34}px)"><b style="width:${h * 0.58}px;height:${h * 0.07}px"></b></i></span>`;
}
